import { Color3, PhysicsMotionType, PhysicsShapeType, Vector3 } from '@babylonjs/core';
import { Asset, AssetType } from '../Asset';
import { AssetObject } from '../AssetObject';
import { DecorationConfig } from '../DecorationGenerator';
import { DungeonConfig, RoomType } from '../DungeonGenerator';
import { EnemyConfig } from '../EnemyGenerator';
import { EnemyPrefabs } from '../EnemyPrefabs';
import { FogConfig } from '../FogConfig';
import { Level } from '../Level';
import {
  CameraConfig,
  CollectibleDistributionConfig,
  LevelConfig,
  MaterialConfig,
  PathConfig,
  PlayerConfig,
  PostProcessConfig,
  SoundConfig,
} from '../LevelConfig';
import { LightConfig } from '../LightConfig';
import { HeightMapConfig, SkyBoxConfig } from '../MeshFactory';
import { ObstacleConfig } from '../ObstacleGenerator';
import { ObstaclePrefabs } from '../ObstaclePrefabs';
import { TextureAtlas } from '../TextureAtlas';
import { LevelTemplate } from './LevelTemplate';

export class TemplateDungeon implements LevelTemplate {
  create(): LevelConfig {
    const probabilityMin = 0.05;
    const probabilityMid = 0.1;
    const probabilityMax = 0.4;

    const fogConfig = new FogConfig();
    const lightConfig = new LightConfig();
    lightConfig.intensity /= 2;
    lightConfig.hemiIntensity /= 1.5;
    lightConfig.hemiGroundColor = new Color3(1, 1, 1);
    lightConfig.diffuse = new Color3(1, 1, 0);
    lightConfig.lensflare = false;

    const skyboxConfig: SkyBoxConfig = {
      url: 'assets/skybox/space_laser.png',
      rotationAxis: 'y',
      rotationSpeed: 0.00001,
    };

    const postProcessConfig: PostProcessConfig = {
      fxaa: false,
      motionBlur: 0,
      //blur: { kernel: 32, strength: 1, x: 1, y: 0 },
      highlights: false,
      sepia: false,
      fireflies: 0,
      snow: 0,
      rain: 0,
    };

    const dungeonHallTextureName = 'Rocks1';
    const dungeonCorridorTextureName = 'Plank2';

    const materialConfig: MaterialConfig = {
      colorMaterialBaseColor: new Color3(0.5, 0.7, 0.9),
      colorMaterialCount: 10,
      textures: [
        { url: 'assets/textures/road_chappy.jpg', uScale: 1, vScale: 1 },
        { url: 'assets/textures/dirt-ground2.avif', uScale: 1, vScale: 1 },
        { url: 'assets/textures/dirt-ground.avif', uScale: 4, vScale: 0.5 },
        { url: 'assets/textures/green-grass.jpg', uScale: 4, vScale: 0.5 },
        { url: 'assets/textures/stone-ground.jpg', uScale: 2, vScale: 2 },
        { url: 'assets/textures/stone-ground2.jpg', uScale: 2, vScale: 2 },
        { url: 'assets/textures/stone-ground-colored.jpg', uScale: 3, vScale: 3 },
        { url: 'assets/textures/wood-ground.jpg', uScale: 2, vScale: 0.3 },

        { url: 'assets/textures/cartoon2/' + dungeonHallTextureName + '_Dark.png', uScale: 1, vScale: 1 },
        { url: 'assets/textures/cartoon2/' + dungeonHallTextureName + '_Light.png', uScale: 1, vScale: 1 },
        { url: 'assets/textures/cartoon2/' + dungeonHallTextureName + '_Medium.png', uScale: 1, vScale: 1 },
        { url: 'assets/textures/cartoon2/' + dungeonCorridorTextureName + '_Dark.png', uScale: 1, vScale: 1 },
        { url: 'assets/textures/cartoon2/' + dungeonCorridorTextureName + '_Light.png', uScale: 1, vScale: 1 },
        { url: 'assets/textures/cartoon2/' + dungeonCorridorTextureName + '_Medium.png', uScale: 1, vScale: 1 },
      ],
      globalTextureNames: [],
      materials: [],
    };

    const pathConfig: PathConfig = {
      start: new Vector3(0, 0, 0),
      destination: new Vector3(0, 0, 500),
      pathPoints: 10,
      segmentsPerCurve: 15,
      pathCurvature: 1.5,
      pathHilliness: 0.5,
      pathScale: 3.0,
      pathMinCurveAngle: 90,
      laneWidth: 3,
      spaceAround: 50,
    };

    const soundConfig: SoundConfig = {
      backgroundMusic: 'sunnySkies',
      backgroundMusicVolume: 1.0,
    };

    const cameraConfig: CameraConfig = {
      alignToPath: false,
      distance: 8,
      heightOffset: 1.2,
      smoothing: 40,
      alphaSmoothing: 1,
      collision: false,
      orthographic: false,
      orthographicZoom: 8,
      orthographicAlpha: Math.PI / 10,
      orthographicBeta: Math.PI / 3,
    };

    const collectibleConfig: CollectibleDistributionConfig = {
      offset: new Vector3(0, 1, 0),
      probability: 0.1,
      minPoint: 10,
      mixPatternTypes: false,
      maxPatternSize: 3,
      spawnRange: 60,
      despawnRange: 90,
    };

    const groundWidth = 250;
    const groundHeight = 500;
    const groundSubdivisions = 50;
    const ceilingConfig = HeightMapConfig.builder()
      .name('ceiling')
      .heightMapImageUrl('assets/heightmap/ambientocclusion_inverted.jpg')
      .width(groundWidth)
      .height(groundHeight)
      .updatable(true)
      .subdivisions(groundSubdivisions)
      .castShadows(false)
      .receiveShadows(false)
      .maxHeight(30)
      .minHeight(0)
      .metadata({
        centerOnPath: true,
      })
      .terrainTextureAtlas('cartoon1')
      .terrainTextureName1('Dirt')
      .terrainTextureName2('Magma')
      .terrainTextureName3('Dirt')
      .rotation(new Vector3(Math.PI, 0, 0)) // rotate 180 degrees
      .terrainTextureScale(new Vector3(100, 100, 100))
      .backFaceCulling(true)
      .diffuseColor(new Color3(1, 1, 1))
      .physicsShapeType(PhysicsShapeType.MESH)
      .physicsMotionType(PhysicsMotionType.ANIMATED)
      .position(new Vector3(0, 17.5, 0))
      .build();

    const wayWidth = 10;
    const decorationConfig: DecorationConfig = {
      assets: [
        new Asset('stones', AssetType.MESH, 'assets/model/nature/', 'path_stone.glb'),
        new Asset('dirt', AssetType.MESH, 'assets/model/nature/', 'crops_dirtRow.glb'),
        new Asset('statue', AssetType.MESH, 'assets/model/arena/', 'statue.glb'),

        // new Asset('flowerYellow', AssetType.MESH, 'assets/model/nature/', 'flower_yellowA.glb'),
        // new Asset('flowerRed', AssetType.MESH, 'assets/model/nature/', 'flower_redB.glb'),
        // new Asset('flowerPurple', AssetType.MESH, 'assets/model/nature/', 'flower_purpleC.glb'),
        // new Asset('banner', AssetType.MESH, 'assets/model/arena/', 'banner.glb'),
        // new Asset('bricks', AssetType.MESH, 'assets/model/arena/', 'bricks.glb'),
        // new Asset('column', AssetType.MESH, 'assets/model/dungeon/', 'column.glb'),
        // new Asset('trophy', AssetType.MESH, 'assets/model/arena/', 'trophy.glb'),
        // new Asset('workbench', AssetType.MESH, 'assets/model/survival/', 'workbench-anvil.glb'),
        // new Asset('barrel', AssetType.MESH, 'assets/model/dungeon/', 'barrel.glb'),
        // new Asset('chest', AssetType.MESH, 'assets/model/dungeon/', 'chest.glb'),

        new Asset('barrel_decorated', AssetType.MESH, 'assets/model/dungeon_shiny/', 'barrel_large_decorated.gltf'),
        new Asset('bed_decorated', AssetType.MESH, 'assets/model/dungeon_shiny/', 'bed_decorated.gltf'),
        new Asset('box_small_decorated', AssetType.MESH, 'assets/model/dungeon_shiny/', 'box_small_decorated.gltf'),
        new Asset('box_stacked', AssetType.MESH, 'assets/model/dungeon_shiny/', 'box_stacked.gltf'),
        new Asset('coin_stack_large', AssetType.MESH, 'assets/model/dungeon_shiny/', 'coin_stack_large.gltf'),
        new Asset('table_long_decorated_C', AssetType.MESH, 'assets/model/dungeon_shiny/', 'table_long_decorated_C.gltf'),
        new Asset('candle_triple', AssetType.MESH, 'assets/model/dungeon_shiny/', 'candle_triple.gltf'),
        new Asset('chest_gold', AssetType.MESH, 'assets/model/dungeon_shiny/', 'chest_gold.gltf'),
        new Asset('crates_stacked', AssetType.MESH, 'assets/model/dungeon_shiny/', 'crates_stacked.gltf'),
      ],
      assetObjects: [
        new AssetObject('dirt').withType('dirt'),
        new AssetObject('stones').withType('stones'),
        new AssetObject('statue').withType('statue'),

        // new AssetObject('flowerYellow').withType('flowers'),
        // new AssetObject('flowerRed').withType('flowers'),
        // new AssetObject('flowerPurple').withType('flowers'),
        // new AssetObject('banner').withType('dungeon'),
        // new AssetObject('bricks').withType('dungeon'),
        // new AssetObject('trophy').withType('dungeon'),
        // new AssetObject('workbench').withType('dungeon'),
        // new AssetObject('barrel').withType('dungeon'),
        // new AssetObject('chest').withType('dungeon'),
        // new AssetObject('column').withType('dungeon'),

        new AssetObject('barrel_decorated').withType('dungeon_detail'),
        new AssetObject('bed_decorated').withType('dungeon_detail'),
        new AssetObject('box_small_decorated').withType('dungeon_detail'),
        new AssetObject('box_stacked').withType('dungeon_detail'),
        new AssetObject('coin_stack_large').withType('dungeon_detail'),
        new AssetObject('table_long_decorated_C').withType('dungeon_detail'),
        new AssetObject('candle_triple').withType('dungeon_detail'),
        new AssetObject('chest_gold').withType('dungeon_detail'),
        new AssetObject('crates_stacked').withType('dungeon_detail'),
      ],
      distributions: [
        // {
        //   types: ['flowers'],
        //   mode: 'mesh',
        //   meshSurface: 'sides',
        //   spread: 0.3,
        //   density: 5,
        //   interval: 1,
        //   rotationY: { min: 0, max: Math.PI * 2 },
        //   scale: { min: 1.8, max: 3.6 },
        //   skipProbability: 0,
        //   skipOccupied: false,
        //   rolling: false,
        //   minPoint: 2,
        // },

        {
          types: ['statue'],
          mode: 'mesh',
          meshSurface: 'center',
          spread: 0.3,
          density: 1,
          probability: 0.05,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1.8, max: 3.6 },
          skipProbability: 0,
          skipOccupied: false,
          physics: true,
          minPoint: 2,
        },

        // {
        //   types: ['dungeon'],
        //   mode: 'mesh',
        //   meshSurface: 'sides',
        //   spread: 0.3,
        //   density: 1,
        //   interval: 1,
        //   rotationY: { min: 0, max: Math.PI * 2 },
        //   scale: { min: 1.8, max: 3.6 },
        //   skipProbability: 0,
        //   skipOccupied: false,
        //   rolling: false,
        //   minPoint: 2,
        // },

        {
          types: ['dungeon_detail'],
          mode: 'mesh',
          meshSurface: 'sides',
          spread: 0.3,
          density: 1,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 0.8, max: 0.8 },
          skipProbability: 0,
          skipOccupied: false,
          rolling: false,
          minPoint: 2,
        },

        {
          types: ['stones'],
          mode: 'zone',
          alignToPath: true,
          offset: new Vector3(0, 0.1, 0),
          zone: { min: -wayWidth / 2.5, max: wayWidth / 2.5 },
          density: 1,
          interval: 4,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1.8, max: 3.6 },
          skipOccupied: true,
          skipProbability: 0.2,
        },
      ],
    };

    const minPoint = 10;
    const obstacleConfig: ObstacleConfig = ObstaclePrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: 6,
      spawnRange: 60,
      despawnRange: 90,
      obstacleColor: 'yellow',
    });

    const enemyConfig: EnemyConfig = EnemyPrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: 6,
      spawnRange: 60,
      despawnRange: 90,
    });

    const divide = new Vector3(2.75, 2.75, 2.75);
    const dungeonConfig: DungeonConfig = {
      roomCount: 20,
      // Vector3-based sizes (x=width, y=height, z=depth)
      hallMinSize: new Vector3(50, 30, 50).divide(divide),
      hallMaxSize: new Vector3(100, 50, 100).divide(divide),
      corridorMinSize: new Vector3(30, 20, 30).divide(divide),
      corridorMaxSize: new Vector3(40, 30, 60).divide(divide),
      wallThickness: 0.25,
      pathCurveShiftFactor: 0.25,
      pathSegmentsPerCurve: 4,
      // Maximum number of connections (corridors) per room
      maxConnectionsPerRoom: 2,
      // If true, new rooms will use a different direction than the parent's last direction
      forceDirectionChange: false,
      wallTextureScale: 0.1,
      ceilingTextureScale: 0.1,
      floorTextureScale: 0.1,
      // Material mapping for different room types: [floor, wall, ceiling]
      roomTypeMaterials: new Map([
        [
          RoomType.CORRIDOR,
          {
            floor: dungeonCorridorTextureName + '_Medium',
            left: dungeonCorridorTextureName + '_Light',
            right: dungeonCorridorTextureName + '_Light',
            front: dungeonCorridorTextureName + '_Light',
            back: dungeonCorridorTextureName + '_Light',
            ceiling: dungeonCorridorTextureName + '_Dark',
          },
        ],
        [
          RoomType.HALL,
          {
            floor: dungeonHallTextureName + '_Medium',
            left: dungeonHallTextureName + '_Light',
            right: dungeonHallTextureName + '_Light',
            front: dungeonHallTextureName + '_Light',
            back: dungeonHallTextureName + '_Light',
            ceiling: dungeonHallTextureName + '_Dark',
          },
        ],
      ]),
    };

    const playerConfig: PlayerConfig = {
      startPathIndex: 4,
      startOffset: new Vector3(0, 3, 0),
      autoRun: 0,
      speed: 10,
      jumpSpeed: 11,
      backwardSpeed: 4,
      turnSpeed: 10,
      dashSpeed: 12,
      projectileSpeed: 20,
    };

    const atlasConfig = [
      new TextureAtlas('tiles2', 'assets/atlas/', 'tiles2.png', 'tiles2.json'),
      new TextureAtlas('cartoon1', 'assets/atlas/', 'cartoon1.png', 'cartoon1.json'),
    ];

    const config = new LevelConfig(
      'Test Level',
      2, // seed
      100, // timelimit
      Level.fullGravity, // gravity
      -45, // killing height
      100, // height above ground
      40, // checkpoint interval
      4, // goal end offset
      playerConfig,
      cameraConfig,
      pathConfig,
      lightConfig,
      skyboxConfig,
      materialConfig
    );

    config.sound = soundConfig;
    config.fog = fogConfig;
    config.collectible = collectibleConfig;
    config.postProcess = postProcessConfig;
    config.decoration = decorationConfig;
    config.enemy = enemyConfig;
    config.obstacle = obstacleConfig;
    config.dungeon = dungeonConfig;
    config.atlasses = atlasConfig;
    config.ceiling = ceilingConfig;

    return config;
  }
}
